/**
 * Auth Hook
 * Updated for React Router 7
 */

import * as React from "react"
import { useNavigate } from "react-router"

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
  roles: string[]
  permissions: string[]
}

export interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<User>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined)

// API base URL - empty string means same origin (proxied by Vite)
const API_BASE = ""

// audra-id Phase 2 Layer B. When on, the AuthProvider always calls
// /api/auth on mount (even without a localStorage token) — the
// httpOnly `audra_session` cookie set by id.audra.digital flows to
// flow's backend automatically and the new server-side middleware
// resolves it to a local user. credentials: 'include' forces the
// browser to send the cookie cross-domain.
const AUDRA_ID_ENABLED =
  import.meta.env.VITE_AUDRA_ID_ENABLED === "true"

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const navigateRef = React.useRef<ReturnType<typeof useNavigate> | null>(null)

  // Check for existing session on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("auth_token")
        const storedUser = localStorage.getItem("auth_user")

        // Three paths the cascading checks below cover:
        //   A. localStorage has a token (legacy JWT path) — verify it
        //   B. AUDRA_ID_ENABLED but no localStorage token — try the
        //      cookie path via /api/auth with credentials: 'include'
        //   C. neither — leave user null, AuthGuard bounces to /login

        if (token && storedUser) {
          // Path A: verify the JWT
          try {
            const response = await fetch(`${API_BASE}/api/auth`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              credentials: "include",
            })

            if (response.ok) {
              const data = await response.json()
              setUser(data.user)
              // Update stored user with fresh data
              localStorage.setItem("auth_user", JSON.stringify(data.user))
            } else {
              // Token is invalid, clear storage
              localStorage.removeItem("auth_token")
              localStorage.removeItem("auth_user")
            }
          } catch {
            // Network error, use stored user for offline support
            setUser(JSON.parse(storedUser))
          }
        } else if (AUDRA_ID_ENABLED) {
          // Path B: try the audra-id cookie. credentials: 'include'
          // sends the cross-domain audra_session cookie that
          // id.audra.digital set on Domain=.audra.digital.
          try {
            const response = await fetch(`${API_BASE}/api/auth`, {
              credentials: "include",
            })
            if (response.ok) {
              const data = await response.json()
              setUser(data.user)
            }
            // 401 here is the common "no SSO session yet" case — leave
            // user null and let AuthGuard bounce to /login → audra-id.
          } catch {
            // Network error — silently leave user null; AuthGuard will
            // bounce on first protected route load.
          }
        }
      } catch (error) {
        console.error("Auth check failed:", error)
        localStorage.removeItem("auth_token")
        localStorage.removeItem("auth_user")
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = React.useCallback(async (email: string, password: string): Promise<User> => {
    setIsLoading(true)
    
    try {
      // Call the real auth API
      const response = await fetch(`${API_BASE}/api/auth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        let errorMessage = "Login failed"
        try {
          const error = await response.json()
          errorMessage = error.error || errorMessage
        } catch {
          // If response is not JSON, use status text or default message
          errorMessage = response.statusText || `Server error (${response.status})`
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      
      // Store token and user
      localStorage.setItem("auth_token", data.token)
      localStorage.setItem("auth_user", JSON.stringify(data.user))
      
      setUser(data.user)
      
      return data.user
    } catch (error) {
      console.error("Login failed:", error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = React.useCallback(async () => {
    setIsLoading(true)
    
    try {
      localStorage.removeItem("auth_token")
      localStorage.removeItem("auth_user")
      setUser(null)
      // Use window.location for reliable redirect
      window.location.href = "/login"
    } catch (error) {
      console.error("Logout failed:", error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refreshToken = React.useCallback(async () => {
    const token = localStorage.getItem("auth_token")
    
    if (!token) {
      return
    }

    try {
      const response = await fetch(`${API_BASE}/api/auth`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        localStorage.setItem("auth_user", JSON.stringify(data.user))
      } else {
        // Token expired, logout
        await logout()
      }
    } catch (error) {
      console.error("Token refresh failed:", error)
    }
  }, [logout])

  // Check if user has a specific permission
  const hasPermission = React.useCallback((permission: string): boolean => {
    if (!user?.permissions) return false
    return user.permissions.includes(permission)
  }, [user])

  // Check if user has any of the specified permissions
  const hasAnyPermission = React.useCallback((permissions: string[]): boolean => {
    if (!user?.permissions) return false
    return permissions.some(p => user.permissions.includes(p))
  }, [user])

  const value = React.useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshToken,
      hasPermission,
      hasAnyPermission,
    }),
    [user, isLoading, login, logout, refreshToken, hasPermission, hasAnyPermission]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  
  return context
}

// Utility hook to get the auth token for API calls
export function useAuthToken(): string | null {
  return localStorage.getItem("auth_token")
}
